// src/Pages/UserVideoLessonDetail/Tabs/LessonInfoTab.tsx
import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FavAndWatchLaterVideoLesson } from "../../../Redux/Slices/UserVideoLessons/FavAndWatchLaterVideoLessons";
import avatar from "../images/avatar.png";
import { CourseDetailsVideoLesson } from "../../../Redux/Slices/UserVideoLessons/CourseDetailsVideoLessons";
import { useAppSelector } from "../../../Redux/Store/Hooks";
import { Box, Fade, SvgIcon } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { ReactComponent as Learners } from "../../../Assets/Images/Learners.svg";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import { ReactComponent as WatchLater } from "../../../Assets/Images/Watch_Later.svg";
import { ReactComponent as History } from "../../../Assets/Images/History.svg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CustomSnackbar from "./Snackbar";
// --- ICONS ---
const IconClock = () => <span className="icon-placeholder">🕒</span>;
const IconHeart = () => <span className="icon-placeholder">♡</span>;
const IconBookmark = () => <span className="icon-placeholder">🔖</span>;
const IconLightbulb = () => <span className="icon-placeholder">💡</span>;
const IconBook = () => <span className="icon-placeholder">📖</span>;
const IconQuestion = () => <span className="icon-placeholder">❓</span>;
const IconGlobe = () => <span className="icon-placeholder">🌐</span>;
const IconUsers = () => <span className="icon-placeholder">👥</span>;

interface Lesson {
  id: number;
  title: string;
  description: string;
  completed?: boolean;
  lession_length?: number;
  complete_lession_length?: number;
  video_count?: number;
  duration: string;
  videoUrl: string;
  progress: string;
  watchTime: string;
  averageScore: string;
  studyStreak: string;
  transcription: string;
  is_favorite?: boolean;
  is_watch_later?: boolean;
}

interface LessonInfoTabProps {
  topicId: number;
  currentLesson: Lesson;
  switchToAskElliotTab: (msg?: string) => void;
}

export const LessonInfoTab: FunctionComponent<LessonInfoTabProps> = ({
  topicId,
  currentLesson,
  switchToAskElliotTab,
}) => {
  const [autoPlayNext, setAutoPlayNext] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [isFavorite, setIsFavorite] = useState(
    currentLesson.is_favorite || false
  );
  const [isWatchLater, setIsWatchLater] = useState(
    currentLesson.is_watch_later || false
  );

  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("Something Wrong!");
  const [title, setTitle] = useState<string>("Something Wrong!");
  const topicName = currentLesson.title;
  const name =
    topicName.toLowerCase().includes("writing") ? "Writing" :
      topicName.toLowerCase().includes("reading") ? "Reading" :
        topicName.toLowerCase().includes("abstract") ? "Abstract" :
          topicName.toLowerCase().includes("quantitative") ? "Quantitative" :
            topicName.toLowerCase().startsWith("ar") ? "Arithmetic" :
              topicName;

  const handleShow = (msg: string, title: string) => {
    setMessage(msg);
    setTitle(title);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  const dispatch = useDispatch();

  // Fetch course details dynamically
  useEffect(() => {
    dispatch(
      CourseDetailsVideoLesson({
        topic_id: topicId,
      })
    );
  }, [topicId, currentLesson.id, dispatch]);

  // Get Course Details from Redux
  const { data: CourseDetails } = useAppSelector(
    (state) => state.CourseDetailsVideoLessonsSliceReducer
  );

  useEffect(() => {
    setIsFavorite(currentLesson.is_favorite || false);
    setIsWatchLater(currentLesson.is_watch_later || false);
  }, [currentLesson.is_favorite, currentLesson.is_watch_later]);

  const handleFavoriteClick = () => {
    handleShow(
      isFavorite
        ? `${currentLesson.title} has been added to your favorites`
        : `${currentLesson.title} has been removed from your favorites`,
      isFavorite ? "Removed from Favorites" : "Added to Favorites"
    );
    const updatedFav = !isFavorite;
    setIsFavorite(updatedFav);
    dispatch(
      FavAndWatchLaterVideoLesson({
        video_id: currentLesson.id,
        is_favorite: updatedFav,
        is_watch_later: isWatchLater,
      })
    );
  };

  const handleWatchLaterClick = () => {
    handleShow(
      isWatchLater
        ? `${currentLesson.title} has been added to your watch later list`
        : `${currentLesson.title} has been removed from your watch later list`,
      isWatchLater ? "Added to Watch Later" : "Removed from Watch Later"
    );
    const updatedWatchLater = !isWatchLater;
    setIsWatchLater(updatedWatchLater);
    dispatch(
      FavAndWatchLaterVideoLesson({
        video_id: currentLesson.id,
        is_favorite: isFavorite,
        is_watch_later: updatedWatchLater,
      })
    );
  };

  function formatDuration(seconds: number, type: string) {
    if (type == "HM") {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h  ${minutes.toString().padStart(2, "0")}m`;
    } else if (type == "MS") {
      const minutes = Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0");
      const second = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
      return `${minutes}:${second}`;
    }
  }

  return (
    <>
      <div className="course-main-section">
        <div className="lesson-details-header">
          <h3>{currentLesson.title}</h3>
          <div className="action-buttons">
            <button
              className="action-button-option"
              onClick={handleFavoriteClick}
            >
              <FavoriteBorderOutlinedIcon
                color={isFavorite ? "error" : "inherit"}
              />
              {isFavorite ? "Favorited" : "Favorite"}
            </button>

            <button
              className="action-button-option"
              onClick={handleWatchLaterClick}
            >
              <SvgIcon
                component={WatchLater}
                inheritViewBox
                sx={{ fill: "none" }}
              />{" "}
              {isWatchLater ? "Added" : "Watch Later"}
            </button>
            <CustomSnackbar
              open={open}
              message={message}
              onClose={handleClose}
              title={title}
              autoHideDuration={1500}
            />
          </div>
        </div>

        <p className="lesson-description">{currentLesson.description}</p>

        <div className="lesson-meta">
          <div className="meta-item">
            <AccessTimeIcon />{" "}
            <span>{formatDuration(Number(currentLesson.duration), "MS")}</span>
          </div>
          <div className="meta-item">
            <span>
              Lesson {currentLesson.video_count} of{" "}
              {currentLesson.lession_length}
            </span>
          </div>
        </div>

        <div className="help-cta-banner banner--compact">
          <div className="banner-content1">
            <div className="banner-image">
              <img src={avatar} alt="Elliot avatar" className="elliot-avatar" />
            </div>
            <div className="banner-text">
              <p className="banner-text-heading">Need help with this lesson?</p>
              <span className="banner-text-description">
                Elliot can explain concepts, provide {name} strategies, and
                answer questions about {currentLesson.title.toLowerCase()}.
              </span>
            </div>
          </div>
          <div className="cta-buttons">
            {/* <div className="cta-div-block"> */}
            <button
              className="cta-buttons-aactions"
              onClick={() => switchToAskElliotTab("Explain this concept")}
            >
              <HelpOutlineOutlinedIcon />
              Explain this concept
            </button>
            <button
              className="cta-buttons-aactions"
              onClick={() => switchToAskElliotTab(`${name} strategies`)}
            >
              <LightbulbOutlinedIcon /> {name} strategies
            </button>
            {/* </div>
            <div className="cta-div-block"> */}
            <button
              className="cta-buttons-aactions"
              onClick={() => switchToAskElliotTab("Ask questions")}
            >
              <ChatBubbleOutlineOutlinedIcon /> Ask questions
            </button>
            {/* </div> */}
          </div>
        </div>

        {/* <div className="autoplay-toggle-wrapper">
          <div className="autoplay-toggle">
            <input
              type="checkbox"
              id="autoplay-next"
              checked={autoPlayNext}
              onChange={(e) => setAutoPlayNext(e.target.checked)}
            />
            <label htmlFor="autoplay-next">Auto-play next lesson</label>
          </div>
        </div> */}

        <div className="show-transcript">
          <button
            className={showTranscript ? "open" : ""}
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? "Hide Transcript" : "Show Transcript"}{" "}
            <span className="arrow">
              <ExpandMoreOutlinedIcon />
            </span>
          </button>

          {showTranscript && (
            <div className="transcript-content">
              <p>
                {currentLesson.transcription || "Transcript not available."}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Dynamic Course Details */}
      <div className="course-main-content-section">
        <Box className="course-details-section">
          <h4>Course Details</h4>
          <div className="course-details-grid">
            <div className="detail-item">
              <span className="detail-label">Total Lessons</span>
              <span className="detail-value">
                {CourseDetails?.data?.video_count ?? "N/A"}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Duration</span>
              <span className="detail-value">
                {/* {CourseDetails?.data?.total_duration
                  ? `${Math.floor(CourseDetails.data.total_duration / 60)}`
                  : "N/A"} */}

                {formatDuration(CourseDetails?.data?.total_duration ?? 0, "HM")}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Language</span>
              <span className="detail-value">
                <LanguageOutlinedIcon sx={{ fontSize: "11.9px" }} />{" "}
                {CourseDetails?.data?.languages?.join(", ") || "N/A"}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Learners</span>
              <span className="detail-value">
                <SvgIcon
                  component={Learners}
                  inheritViewBox
                  sx={{ fill: "none", fontSize: "11.9px" }}
                />{" "}
                {/* If learners count comes from API, use it; else placeholder */}
                {CourseDetails?.data?.learners ?? 0}
              </span>
            </div>
          </div>
        </Box>
      </div>
    </>
  );
};
